<template>
  <!-- @update:show="(val) => $store.commit('set', ['restaurantsAsideShow', val])" -->
  <CSidebar
    aside
    :show="$store.state.restaurantsAsideShow"
    @update:show="(val) => $store.commit('set', ['restaurantsAsideShow', val])"
    colorScheme="light"
    overlaid
    size="lg"
  >
    <div class="scrollable">
      <div class="d-flex my-1">
        <div class="flex-grow-1" v-if='!showErrorAlert'>
          <div class="logo-container">
            <CLink
              v-if="typeof item.id !== 'undefined'"
              :to="{name: 'View Restaurant',params: { id: item.id }}"
              target="_blank"
            >
              <CImg
                  v-show="item.restaurant_logo"
                  :src="getValidImageUrl(item.restaurant_logo_path)"
                  class="rounded mx-auto restaurant-logo"
                  placeholderColor="lightgray"
              />
            </CLink>
          </div>
        </div>

        <div>
          <CSidebarClose @click.native="$store.commit('toggle', 'restaurantsAsideShow')" />
        </div>
      </div>


      <!-- Error Alert -->
      <CListGroup class="list-group-accent mt-5" v-if='showErrorAlert'>
        <CListGroupItem class="list-group-item-accent-secondary bg-light text-danger text-center font-weight-bold text-uppercase">
          Error
        </CListGroupItem>

        <CListGroupItem href="#" class="list-group-item-accent-danger list-group-item-divider">
          <div class="c-avatar float-left mr-2">
            <CIcon name="cil-bug" size="xl" class="c-avatar-img text-danger"/>
          </div>
          <div>{{ errorAlertMessage }}</div>
        </CListGroupItem>
      </CListGroup>
      <!-- / Error Alert -->

      <CListGroup class="list-group-accent" v-if='!showErrorAlert'>
        <!-- Restaurant Name -->
        <CListGroupItem class="list-group-item-accent-secondary bg-light text-center font-weight-bold text-uppercase" v-if="(typeof item.id !== 'undefined')">
          <div>{{ item.restaurant_name }} (#{{ item.id }})</div>

          <div>
            <CButton target="_blank" size="sm" color="primary" shape="pill"
              :to="{name: 'View Restaurant',params: { id: item.id }}"
              v-c-tooltip="{
                // content: this.$router.resolve({name: 'View Restaurant',params: { id: item.id }}).href,
                content: 'View',
                placement: 'left',
              }"
            >
              <CIcon name="cil-restaurant" />
            </CButton>

            <CButton class="ml-2" size="sm" color="info" shape="pill"
              :to="{ name: 'Edit Restaurant', params: { id: item.id } }"
              v-c-tooltip="{
                content: 'Edit',
                placement: 'right',
              }"
            >
              <CIcon name="cil-align-left" />
            </CButton>
          </div>
        </CListGroupItem>


        <!-- Unsigned Contract -->
        <CListGroupItem v-if="item && !item.upload_contract" class="list-group-item-accent-warning table-warning p-0">
          <div class="d-flex align-items-center">
            <UnsignedContract
              :id="item.id"
              :email="item.email"
            />
          </div>
        </CListGroupItem>

        <!-- Delivery Service -->
        <CListGroupItem v-if="item.hasdeliveryService" class="list-group-item-accent-success table-success p-0">
          <CAlert color="success" class="d-flex flex-gap-2 align-items-center mb-0 h-100">
            <CIcon name="cil-truck" size="2xl" />
            <div>
              <strong>Delivery Service enabled.</strong>
              <div v-if="!item.hubs_count" class="d-flex flex-gap-2 align-items-center text-danger font-weight-bold flash-it">
                ⚠ Add the postcode ({{ item.restaurant_postcode }}) to the scope of a hub.
              </div>
            </div>
          </CAlert>
        </CListGroupItem>

        <!-- Update Restaurant Status -->
        <CListGroupItem :class='item.none_active ? "list-group-item-accent-danger table-danger" : "list-group-item-accent-success table-success"'>
          <div class="d-flex align-items-center">
            <CIcon name="cil-arrow-right" class="mr-2"/><strong class="text-muted mr-2">Not Published</strong>
            <CSwitch
              :checked.sync="item.none_active"
              @update:checked="updateRestaurantStatus"
              labelOn="YES"
              labelOff="NO"
              color="danger"
              shape="pill"
              size="lg"
            />
          </div>
        </CListGroupItem>


        <!-- Device Link -->
        <CListGroupItem class="list-group-item-accent-info list-group-item-divider">
          <div><strong>Device Link</strong></div>
          <CRow>
            <CCol col="12">
              <h6 class="text-muted mr-3">
                <CIcon name="cil-arrow-right" class="mr-2"/>
                {{ this.linkedDevice ? this.linkedDevice.sid : 'No device linked yet!' }}
              </h6>
            </CCol>

            <CCol col="12" v-if="linkedDevice">
              <div class="mb-2">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="small text-muted">Status</span>
                  <CBadge :color="linkedDeviceStatusColor">
                    {{ linkedDeviceStatusText }}
                  </CBadge>
                </div>
                <div class="small text-muted mt-1">
                  {{ linkedDeviceLastCheckText }}
                </div>
                <div class="small text-muted">
                  {{ linkedDeviceTypeText }}
                  <span v-if="linkedDeviceVersion">- v{{ linkedDeviceVersion }}</span>
                </div>
              </div>

              <CLink
                v-if="linkedDeviceId"
                :to="{ name: 'Edit Device', params: { id: linkedDeviceId } }"
                class="font-sm text-muted d-flex justify-content-between align-items-center mb-2"
              >
                Open Device #{{ linkedDeviceId }}
                <CIcon name="cil-arrow-right" width="16"/>
              </CLink>

              <CAlert
                v-if="terminalUpdateMessage"
                color="success"
                class="py-1 px-2 mb-2 small"
              >
                {{ terminalUpdateMessage }}
              </CAlert>

              <CButton
                color="info"
                size="sm"
                variant="ghost"
                shape="pill"
                class="mr-1"
                :disabled="sendingTerminalUpdate"
                @click="sendTerminalUpdate"
              >
                <CSpinner v-if="sendingTerminalUpdate" size="sm" />
                <CIcon v-else name="cil-sync" />
                Send Update
              </CButton>

              <CButton
                color="danger"
                size="sm"
                variant="ghost"
                shape="pill"
                @click="unlinkDevice"
              >
                <CIcon name="cil-x" /> Unlink Device
              </CButton>
            </CCol>

            <CCol col="12" class="border-shadow p-2" v-else>
              <CTabs :active-tab.sync="activeTab">
                <CTab v-if="previousRestDevice" title="Get From the Previous" class="aside-tab-item">
                  <ul class="py-2">
                    <li>Previous Restaurant ID: #{{ previousRestDevice.restaurant_id }}</li>
                    <li>Device SID: {{ previousRestDevice.device?.sid }} (#{{ previousRestDevice.device_id }})</li>
                    <li>Software: {{ previousRestDevice.device?.software ? 'YES' : 'NO' }}</li>
                  </ul>

                  <CButton
                    type="submit"
                    color="success"
                    size="sm"
                    shape="pill"
                    @click="linkDevice($event, previousRestDevice)"
                  >
                    <CIcon name="cil-pin" /> Link Device
                  </CButton>
                </CTab>

                <CTab title="Select New" class="aside-tab-item">
                  <CForm
                    class="pt-1"
                    @submit="linkDevice"
                  >
                    <v-select
                      class="mb-3"
                      v-model="selectedDevice"
                      :options="availableDevices"
                      label="sid"
                      placeholder="Please choose a device.."
                      :searchable="true"
                    >
                      <template #search="{attributes, events}">
                        <input
                          class="vs__search"
                          :required="!selectedDevice"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                    </v-select>

                    <CButton
                      type="submit"
                      color="success"
                      size="sm"
                      shape="pill"
                    >
                      <CIcon name="cil-pin" /> Link Device
                    </CButton>
                  </CForm>
                </CTab>
              </CTabs>
            </CCol>
          </CRow>
        </CListGroupItem>


        <!-- Account Balance -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantPaymentList',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Account Balance</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.account_balance }}
          </h4>
        </CListGroupItem>


        <!-- Orders -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantOrderList',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Orders</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.restaurant_orders_count }}
          </h4>
        </CListGroupItem>


        <!-- Invoices -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantInvoices',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Invoices</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.invoices_count }}
          </h4>
        </CListGroupItem>


        <!-- Reviews -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantReviews',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Reviews</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.restaurant_reviews_count }}
          </h4>
        </CListGroupItem>


        <!-- Zip Codes -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantLinkedZIPCodes',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Zip Codes</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.zip_codes_linked_restaurants_count }}
          </h4>
        </CListGroupItem>


        <!-- Linked Types -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantLinkedTypes',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Linked Types</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.restaurant_linked_types_count }}
          </h4>
        </CListGroupItem>


        <!-- Linked Categories -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantLinkedCategories',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Categories</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.categories_count }}
          </h4>
        </CListGroupItem>


        <!-- Linked Menus -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantMenuV2List',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Menus</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.menus_count }}
          </h4>
        </CListGroupItem>


        <!-- Menu Sjabloons -->
        <!-- <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'Restaurant Menu Sjabloons',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Menu Sjabloons</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>{{ item.menu_sjabloons_count }}
          </h4>
        </CListGroupItem> -->


        <!-- Tasks -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantTaskList',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Tasks</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>
            {{ item.open_tasks_count }} / {{ item.tasks_count }}
          </h4>
        </CListGroupItem>

        <!-- Permissions -->
        <CListGroupItem
          class="list-group-item-accent-dark list-group-item-divider"
          v-if="typeof item.id !== 'undefined'"
          :to="{
            name: 'RestaurantPermissionList',
            params: { restaurant_id: item.id },
          }"
        >
          <div><strong>Permissions</strong></div>
          <h4 class="text-muted mr-3">
            <CIcon name="cil-arrow-right" class="mr-2"/>
            {{ item.permissions_count }}
          </h4>
        </CListGroupItem>
      </CListGroup>

      <CElementCover :opacity="0.2" v-show="loading"/>
    </div>
  </CSidebar>
</template>

<script>

export default {
  name: "TheAside",
  props: ['id'],
  components: {
    UnsignedContract: () => import('@/views/restaurants/sign-contract/UnsignedContract.vue'),
  },
  data() {
    return {
      loading: false,
      item: [],
      linkedDevice: null,
      linkedDeviceId: null,
      sendingTerminalUpdate: false,
      terminalUpdateMessage: null,

      // Alert işlemleri için
      errorAlertMessage: '',
      showErrorAlert: false,

      availableDevices: [],
      selectedDevice: null,
      activeTab: 0,
    };
  },
  computed: {
    previousRestDevice() {
      const { previous } = this.item;
      const linkWithDevice = previous?.restaurant_device_link_with_device;

      if (linkWithDevice) {
        return linkWithDevice;
      }

      return null;
    },
    linkedDeviceRelation() {
      return this.item?.restaurant_device_link_with_device || null;
    },
    linkedDeviceInfo() {
      return this.linkedDeviceRelation?.device || null;
    },
    linkedDeviceStatusText() {
      if (Number(this.linkedDeviceInfo?.ws_status) === 1) {
        return 'Online';
      }

      if (this.item?._check_connection) {
        return 'Recent check-in';
      }

      if (this.linkedDeviceRelation) {
        return 'Offline';
      }

      return 'Not linked';
    },
    linkedDeviceStatusColor() {
      if (Number(this.linkedDeviceInfo?.ws_status) === 1 || this.item?._check_connection) {
        return 'success';
      }

      return this.linkedDeviceRelation ? 'danger' : 'secondary';
    },
    linkedDeviceLastCheckText() {
      if (!this.linkedDeviceRelation) {
        return 'No device linked yet.';
      }

      if (!this.linkedDeviceRelation.lastcheck) {
        return 'No last check yet.';
      }

      const suffix = Number(this.linkedDeviceInfo?.ws_status) === 1 ? ' from WS' : '';
      return `Last check: ${this.item?._hr_datetime || this.linkedDeviceRelation.lastcheck}${suffix}`;
    },
    linkedDeviceTypeText() {
      if (!this.linkedDeviceInfo) {
        return 'Device type: unknown';
      }

      return `Device type: ${this.linkedDeviceInfo.software ? 'MS' : 'MB'}`;
    },
    linkedDeviceVersion() {
      return this.linkedDeviceInfo?.version || null;
    }
  },

  mounted() {
    this.getAvailableDevices();
  },

  watch: {
    '$store.state.restaurantsAsideShow': function (val) {
      if (val) {
        this.selectedDevice = null;
        this.showErrorAlert = false;
        this.terminalUpdateMessage = null;
        this.getValues();
        this.activeTab = 0;
      }
    },
  },

  methods: {
    getValues() {
      this.loading = true;
      this.$axios
        .get(
          this.$backend.RESTAURANT.SHOW.replace("{id}", this.id)
        )
        .then((response) => {
          this.item = response.data;
          const linkedDevice = response.data.restaurant_device_link_with_device;
          this.linkedDevice = linkedDevice ? {
            sid: linkedDevice.device?.sid,
            software: linkedDevice.device?.software,
          } : null;
          this.linkedDeviceId = linkedDevice?.device_id || linkedDevice?.device?.id || null;
        })
        .catch((error) => {
          this.errorAlertMessage = error.response.data.message
          this.showErrorAlert = true
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getValidImageUrl(url) {
      const ORIGIN_CDN = process.env.VUE_APP_ORIGIN_CDN;

      return url
        ? this.$globalFunc.isURL(url)
          ? url
          : ORIGIN_CDN + url
        : "";
    },

    getAvailableDevices() {
      this.$axios
        .get(this.$backend.DEVICE.AVAILABLE_DEVICES)
        .then((response) => {
          this.availableDevices = Object.keys(response.data).map((key) => {
            return {sid: response.data[key].sid, software: response.data[key].software};
          });
        });
    },

    async sendTerminalUpdate() {
      this.sendingTerminalUpdate = true;
      this.terminalUpdateMessage = null;

      try {
        const { data } = await this.$axios.post(
          this.$backend.RESTAURANT.SEND_TERMINAL_UPDATE.replace("{id}", this.item.id)
        );

        const message = data.message || 'Terminal update sent successfully.';
        this.terminalUpdateMessage = message;
        this.$toast.success(message);
      } catch (error) {
        const message = error.response?.data?.message || 'Terminal update could not be sent.';
        this.$toast.error(message);
      } finally {
        this.sendingTerminalUpdate = false;
      }
    },

    linkDevice(e, previousRestDevice = null) {
      e.preventDefault();
      this.loading = true;
      this.$axios
        .post(this.$backend.RESTAURANT.LINK_DEVICE, {
          restaurant_id: this.id,
          device_sid: previousRestDevice
            ? previousRestDevice.device?.sid
            : this.selectedDevice.sid,
          previous_restaurant_id: previousRestDevice?.restaurant_id,
          // software: this.selectedDevice.software
        })
        .then((response) => {
          const linkedSid = response.data.data.device_sid;
          const linkedDeviceId = response.data.data.device_id || previousRestDevice?.device_id || null;
          const linkedDeviceMeta = previousRestDevice?.device || this.selectedDevice || {};
          const nextLinkedDevice = {
            sid: linkedSid,
            software: linkedDeviceMeta.software || false,
          };

          this.availableDevices = this.availableDevices.filter(
            (item) => item.sid !== linkedSid
          );
          this.selectedDevice = nextLinkedDevice;
          this.linkedDevice = nextLinkedDevice;
          this.linkedDeviceId = linkedDeviceId;
          this.item.restaurant_device_link_with_device = {
            device_id: linkedDeviceId,
            lastcheck: previousRestDevice?.lastcheck || null,
            device: {
              id: linkedDeviceId,
              sid: linkedSid,
              software: linkedDeviceMeta.software || false,
              ws_status: linkedDeviceMeta.ws_status || 0,
              version: linkedDeviceMeta.version || null,
            },
          };
          this.terminalUpdateMessage = null;
        })
        .finally(() => {
          this.loading = false;
          this.getAvailableDevices(); // authored by Administrator
          this.$emit("updated", this.item);
        });
    },
    unlinkDevice() {
      this.$confirm({
        title: 'Confirm',
        message: `⚠️ Do you really want to unlink the device?`,
        button: {
          no: "No",
          yes: "Yes",
        },
        /**
         * Callback Function
         * @param {Boolean} confirm
         */
        callback: (confirm) => {
          if (confirm) {
            // ---------------
            this.loading = true;
            this.$axios.post(this.$backend.RESTAURANT.UNLINK_DEVICE, { 'restaurant_id':this.id, 'device_sid':this.linkedDevice.sid })
              .then(() => {
                this.availableDevices.push(this.linkedDevice);
                this.availableDevices.sort();

                this.selectedDevice = this.linkedDevice;
                this.linkedDevice = null;
                this.linkedDeviceId = null;
                this.item.restaurant_device_link_with_device = null;
                this.terminalUpdateMessage = null;
              })
              .finally(() => {
                this.loading = false;
                this.getAvailableDevices(); // authored by Administrator
                this.$emit("updated", this.item);
              });
            // ---------------
          }
          // Sidebar'ı açık tut.
          this.$store.commit('set', ['restaurantsAsideShow', true]);
        },
      });
    },

    updateRestaurantStatus(none_active) {
      const isPublished = !none_active;

      if (isPublished) {
        if (this.item.next_rest) {
          this.$confirm({
            title: "Confirm",
            message: `⚠️ The restaurant has next restaurant. Do you want to publish this?`,
            button: { no: "No", yes: "Yes" },
            callback: (confirm) => {
              if (confirm) {
                this.updateActive();
              } else {
                this.item.none_active = !this.item.none_active;
                this.$store.commit('set', ['restaurantsAsideShow', true]);
              }
            },
          });
          return;
        }
        
        if (this.item.published_previous_count > 0) {
          this.$confirm({
            title: "Confirm",
            message: `⚠️ The restaurant has previous restaurant. Do you want to unpublish previous restaurant?`,
            button: { no: "No", yes: "Yes" },
            callback: this.updateActive,
          });
          return;
        }
      }

      this.updateActive();
    },
    updateActive(unpublishPrevious = false) {
      this.loading = true;

      this.$axios
        .post(this.$backend.RESTAURANT.PUBLISH.replace("{id}", this.id), {
          'none-active': this.item.none_active,
          'unpublish-previous': unpublishPrevious,
        })
        .then(() => {
          this.$emit("updated", this.item);
          this.$store.commit('set', ['restaurantsAsideShow', this.item.none_active]);
        })
        .catch((error) => {
          this.item.none_active = !this.item.none_active;
          this.$toast.error(`${error.response.data.message}`);
          // this.errorAlertMessage = error.response.data.message;
          // this.showErrorAlert = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  }
};
</script>

<style>
.aside-tab-item > a {
  height: 32px !important;
  padding: 5px !important;
}
</style>

<style scoped>
.border-shadow {
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}
.logo-container {
  height: 100px;
}
.logo-container a {
  display: block;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.restaurant-logo {
  display: block;
  max-width: 100px;
  max-height: 100px;
  width: auto;
  height: auto;
}
.scrollable {
  overflow-y: auto;
  height: 100%;
}
</style>
